import { CreditCard, InProgressTask, Wallet, Warning } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { AccountListItem } from './types'

const getCardVisualization = () => ({
  type: 'card' as const,
  options: {
    title: (item: AccountListItem) => {
      // For active accounts, show balance as title for prominence
      if (item.groupStatus === 'active' && item.balanceText) {
        return item.balanceText
      }
      return item.title
    },
    description: (item: AccountListItem) => {
      // For active accounts, show bank name as description
      if (item.groupStatus === 'active') {
        return item.institutionName || item.title
      }
      // For onboardings, show institution name
      return item.institutionName || ''
    },
    image: (item: AccountListItem) => item.logoUrl || undefined,
    cardProperties: [
      // Status badge (only for onboardings)
      {
        label: '',
        icon: InProgressTask,
        render: (item: AccountListItem) => {
          if (item.groupStatus !== 'in_review') return undefined
          if (!item.statusText) return undefined
          return {
            type: 'status' as const,
            value: {
              status: item.statusVariant || 'neutral',
              label: item.statusText,
            },
          }
        },
      },
      // Account digits (only for active accounts)
      {
        label: '',
        icon: CreditCard,
        render: (item: AccountListItem) => {
          if (!item.accountLastDigits) return undefined
          return {
            type: 'text' as const,
            value: `•••• ${item.accountLastDigits}`,
          }
        },
      },
      // Legal entity
      {
        label: '',
        icon: Wallet,
        render: (item: AccountListItem) => {
          if (!item.description) return undefined
          return {
            type: 'text' as const,
            value: item.description,
          }
        },
      },
      // Alert with action indicator (when action is required)
      {
        label: '',
        icon: Warning,
        render: (item: AccountListItem) => {
          if (!item.hasAlert) return undefined

          const level: 'warning' | 'critical' =
            item.alertLevel === 'critical' ? 'critical' : 'warning'

          // If there's an action, show it with arrow indicator
          if (item.actionText) {
            const displayText = item.isExternalAction ? `${item.actionText} ↗` : item.actionText
            return {
              type: 'alertTag' as const,
              value: {
                label: displayText,
                level,
              },
            }
          }

          // Fallback to alert message only
          if (item.alertMessage) {
            return {
              type: 'alertTag' as const,
              value: {
                label: item.alertMessage,
                level,
              },
            }
          }

          return undefined
        },
      },
    ],
  },
})

// Translation keys for the collection
export const getEmptyStates = () => ({
  'no-data': {
    title: i18n.t('banking.accounts.empty_state.title'),
    description: i18n.t('banking.accounts.empty_state.description'),
  },
})
